import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarPlus, Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/recouvrement`;

const TYPES = [
  { value: 'call', label: 'Appel téléphonique' },
  { value: 'email', label: 'Email de suivi' },
  { value: 'visit', label: 'Visite / RDV' },
  { value: 'recall', label: 'Relance' },
  { value: 'other', label: 'Autre' },
];

const tomorrow = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

/** Création d'une tâche de suivi rattachée à une facture (notifie la personne assignée). */
export const DunningTaskModal = ({ open, invoiceRef, clientName, onClose, onCreated }) => {
  const [assignees, setAssignees] = useState([]);
  const [title, setTitle] = useState('');
  const [type, setType] = useState('call');
  const [assigneeId, setAssigneeId] = useState('');
  const [dueDate, setDueDate] = useState(tomorrow());
  const [dueTime, setDueTime] = useState('09:00');
  const [priority, setPriority] = useState('moyenne');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(`Relancer ${clientName || 'le client'} — facture ${invoiceRef}`);
    axios.get(`${API}/assignees`)
      .then(({ data }) => setAssignees(data.assignees || []))
      .catch(() => toast.error('Liste des collaborateurs indisponible'));
  }, [open, clientName, invoiceRef]);

  const create = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/invoice/${encodeURIComponent(invoiceRef)}/tasks`, {
        title: title.trim(), type, assigneeId, dueDate, dueTime, priority,
        notes: notes.trim() || null,
      });
      toast.success('Tâche créée — la personne assignée a été notifiée');
      onCreated && onCreated();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Création de la tâche impossible');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="rec-task-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <CalendarPlus className="h-4 w-4 text-blue-600" /> Créer une tâche de suivi
          </DialogTitle>
          <DialogDescription className="text-xs">
            Rattachée à la facture {invoiceRef}. La personne assignée reçoit une notification.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Intitulé</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="h-9 text-sm"
              data-testid="rec-task-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="h-9 text-sm" data-testid="rec-task-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Priorité</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className="h-9 text-sm" data-testid="rec-task-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="basse">Basse</SelectItem>
                  <SelectItem value="moyenne">Moyenne</SelectItem>
                  <SelectItem value="haute">Haute</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Assignée à</Label>
            <Select value={assigneeId} onValueChange={setAssigneeId}>
              <SelectTrigger className="h-9 text-sm" data-testid="rec-task-assignee">
                <SelectValue placeholder="Choisir un collaborateur…" />
              </SelectTrigger>
              <SelectContent>
                {assignees.map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.name} · {a.role}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Échéance</Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
                className="h-9 text-sm" data-testid="rec-task-date" />
            </div>
            <div>
              <Label className="text-xs">Heure</Label>
              <Input type="time" value={dueTime} onChange={(e) => setDueTime(e.target.value)}
                className="h-9 text-sm" data-testid="rec-task-time" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Notes (optionnel)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
              className="text-xs" placeholder="Contexte, interlocuteur, engagement pris…"
              data-testid="rec-task-notes" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="rec-task-cancel">Annuler</Button>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={create}
            disabled={saving || !assigneeId || title.trim().length < 3} data-testid="rec-task-create">
            {saving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} Créer la tâche
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DunningTaskModal;
